import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AuthService } from 'app/services/auth.service';
import { StorageKeys } from 'app/constants/common.constants';
import { MatNavList } from '@angular/material/list';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { FormsModule } from '@angular/forms';
import { NavigationItemComponent } from './navigation-item/navigation-item.component';
import { SharingService } from 'app/services/sharing.service';
import { FilterService } from 'app/services/filter.service';
import { ISelectOption } from 'app/models/filter-form.models';
import { IDepartment } from 'app/models/sharing.models';
import { DepartmentService } from 'app/services/department.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  imports: [
    MatNavList,
    NgClass,
    NgIf,
    MatIconButton,
    MatLabel,
    MatIcon,
    NgFor,
    NavigationItemComponent,
    MatFormField,
    MatSelect,
    MatOption,
    FormsModule,
    MatLabel,
    MatHint,
  ],
})
export class NavigationComponent {
  activeLink = '';
  @Input() isExpanded = true;
  @Input() title = '';

  @Input() selectOptions: ISelectOption<number>[] = [];

  private selectedOptionsInternal: number[] = this.preprocessDefaultDepartment();

  get selectedOptions(): number[] {
    return this.selectedOptionsInternal;
  }
  set selectedOptions(val: number[]) {
    this.selectedOptionsInternal = val;
    this.saveSelectedDepartments(val);
  }

  private saveSelectedDepartments(val: number[]): void {
    sessionStorage.setItem(StorageKeys.DEPARTMENTS_SELECTED, JSON.stringify(val));
  }

  private getSelectedDepartments(): number[] {
    return JSON.parse(sessionStorage.getItem(StorageKeys.DEPARTMENTS_SELECTED) || '[]');
  }

  get isAdmin() {
    return this.authService.isAdmin;
  }

  private preprocessDefaultDepartment(): number[] {
    if (this.getSelectedDepartments().length === 0) {
      const departmentId = Number(this.authService.getDepartmentId());
      this.saveSelectedDepartments([departmentId]);
    }
    return this.getSelectedDepartments();
  }

  @Input() navigationItems: {
    title: string;
    icon?: string;
    url?: string;
  }[] = [
    {
      title: 'Map',
      icon: 'map',
      url: 'map',
    },
    {
      title: 'Data',
      icon: 'assignment',
      url: 'base',
    },
    {
      title: 'Analytics',
    },
    {
      title: 'Citations',
      icon: 'location_on',
      url: 'analytic/citations',
    },
    {
      title: 'Persons',
      icon: 'person',
      url: 'analytic/persons',
    },
    {
      title: 'Vehicles',
      icon: 'local_shipping',
      url: 'analytic/vehicles',
    },
    {
      title: 'Tree Map',
      icon: 'space_dashboard',
      url: 'analytic/tree-map',
    },
  ];

  @Input() set currentRoute(value: string) {
    this.activeLink = value;
  }

  @Output()
  beforeNavigate = new EventEmitter();

  @Output()
  departmentSelectionChange = new EventEmitter<number[]>();

  constructor(
    private authService: AuthService,
    private sharingService: SharingService,
    private departmentService: DepartmentService,
    private readonly filterService: FilterService,
  ) {
    this.loadDepartments();

    if (authService.isOfficer) {
      this.navigationItems = [];
    }

    if (!authService.isOfficer) {
      const dataItemIndex = this.navigationItems.findIndex((item) => item.url === 'base');
      this.navigationItems.splice(dataItemIndex + 1, 0, {
        title: 'Totals',
        icon: 'analytics',
        url: 'totals',
      });
    }

    if (this.authService.isOperationsRoles) {
      this.navigationItems.push({
        title: 'Operations',
        icon: 'star_border',
        url: 'analytic/operations',
      });
    }
  }

  loadDepartments(): void {
    if (this.authService.isAdmin) {
      this.departmentService.getDepartments().subscribe((data: { id: string; name: string }[]) => {
        this.selectOptions = data.map((item: { id: string; name: string }) => ({
          viewValue: item.name,
          value: parseInt(item.id),
        }));
      });
    } else {
      this.sharingService.loadDepartmentRelations().subscribe((data: IDepartment[]) => {
        this.selectOptions = data.map((item: IDepartment) => ({ viewValue: item.name, value: item.id }));
      });
    }
  }

  onSelectionChange(): void {
    const currentFilters = this.filterService.appliedFilters;
    if (currentFilters) {
      this.filterService.applyAllFilters(currentFilters);
    }
  }

  get hasSelectOptions(): boolean {
    return this.selectOptions.length > 0;
  }

  logoff(): void {
    this.authService.logout();
    sessionStorage.removeItem(StorageKeys.FILTERS);
    sessionStorage.removeItem(StorageKeys.LAST_OPENED_TAB_INDEX);
    sessionStorage.removeItem(StorageKeys.ALL_DATA_SELECTED_COLUMNS);
    sessionStorage.removeItem(StorageKeys.OPERATIONS_SELECTED_COLUMNS);
    sessionStorage.removeItem(StorageKeys.DEPARTMENTS_SELECTED);
  }
}
